import { useIsDevCycleInitialized } from "@devcycle/react-client-sdk";
import styled from "@emotion/styled";
import { createRoute, Navigate, Outlet, redirect } from "@tanstack/react-router";
import { AccountManagementLayout, DEFAULT_ROUTE } from "pages/app/AccountManagementPage";

import AccountSettingsPage from "components/account_management/account/AccountSettingsPage";
import CognismTokenPage from "components/account_management/cognism_token/CognismTokenPage";
import ContactsSettingsPage from "components/account_management/contacts/ContactsSettingsPage";
import { Integrations } from "components/account_management/Integrations";
import { McpCredentialsPage } from "components/account_management/mcp/McpCredentialsPage";
import { EmailNotifications } from "components/account_management/notification_settings/EmailNotifications";
import NotificationsPage from "components/account_management/notification_settings/NotificationsPage";
import {
  IntegrationIndex,
  IntegrationsNotificationSettings,
} from "components/account_management/notifications/IntegrationNotifications";
import BidSelfServe from "components/account_management/qualification-criteria/BidSelfServe";
import QuickPromptsSettings from "components/account_management/quick_prompts/QuickPromptsSettings";
import SignalSettings from "components/account_management/SignalSettings";
import OidcRegistrationForm from "components/account_management/sso/OidcRegistrationForm";
import ProviderSelector, {
  getPresetByKey,
} from "components/account_management/sso/ProviderSelector";
import { generateProviderId } from "components/account_management/sso/ProviderUtils";
import SamlRegistrationForm from "components/account_management/sso/SamlRegistrationForm";
import SsoSettingsPage from "components/account_management/sso/SsoSettingsPage";
import Subscription from "components/account_management/Subscription";
import ToneOfVoicePage from "components/account_management/tone_of_voice/ToneOfVoicePage";
import TwoFactorSettings from "components/account_management/two_factor_settings/TwoFactorSettings";
import UsagePage from "components/account_management/usage/UsagePage";
import UserManagement from "components/account_management/UserManagement";
import BidWorkflow from "components/account_management/workflow_stages/BidWorkflow";
import LeadWorkflow from "components/account_management/workflow_stages/LeadWorkflow";
import FieldMappingPage from "components/integrations/field_mapping/FieldMappingPage";
import { IntegrationSetup } from "components/integrations/IntegrationSetup";
import { isMcpProviderId, type McpProviderId } from "components/integrations/mcpProviders";
import EmptyState from "components/ui/empty_state/EmptyState";
import { useSession } from "lib/auth/authClient";
import CentredSpinner from "lib/core_components/CentredSpinner";
import {
  RELEVANCY_SEARCH_NEW_USERS,
  TEAM_USAGE_STATS,
  useVariableValue,
} from "lib/featureFlags/generated/dvcVariableTypes";
import { ssoProvidersQueryOptions, useSsoProviders } from "lib/hooks/api/auth/sso/useSsoProviders";
import { currentUserQueryOptions, useAssertCurrentUser } from "lib/hooks/api/users/useCurrentUser";
import { companyHasSubscription } from "lib/hooks/subscription";
import { settingsRoutes } from "lib/routes";
import { SubscriptionEnum } from "lib/types/graphQLEnums";
import EditQualificationReportQuestions from "../../components/account_management/qualification_report/EditQualificationReportQuestions";
import { uploadContextLayoutRoute } from "./uploadContext";

// ---------------------------------------------------------------------------
// /account-management layout — renders SettingsNav + DialogManagerProvider
// noShell: true because this page manages its own navigation sidebar
// ---------------------------------------------------------------------------

export const accountLayoutRoute = createRoute({
  getParentRoute: () => uploadContextLayoutRoute,
  path: "/account-management",
  staticData: { pageName: "", noShell: true },
  // `noShell` (this page owns its own sidebar), but upload progress is still
  // tracked: the DocumentUploadProgressProvider lives in RootLayout, above the
  // shell/noShell split, so integration-sync uploads survive navigation away.
  component: AccountManagementLayout,
});

// /account-management/ → redirect to /account-management/subscription
export const accountIndexRoute = createRoute({
  getParentRoute: () => accountLayoutRoute,
  path: "/",
  staticData: { pageName: "" },
  component: function AccountIndexRedirect() {
    return <Navigate to={DEFAULT_ROUTE} replace />;
  },
});

// /account-management/subscription
export const accountSubscriptionRoute = createRoute({
  getParentRoute: () => accountLayoutRoute,
  path: "/subscription",
  staticData: { pageName: "Account Management - Subscription" },
  component: Subscription,
});

// /account-management/feed-settings
// Redirects to /subscription when the relevancy search flag is enabled (flag disables feed settings)
export const accountFeedSettingsRoute = createRoute({
  getParentRoute: () => accountLayoutRoute,
  path: "/feed-settings",
  staticData: { pageName: "Account Management - Feed Settings" },
  component: function AccountFeedSettingsRouteComp() {
    const user = useAssertCurrentUser();
    const isRelevancySearchEnabled = useVariableValue(RELEVANCY_SEARCH_NEW_USERS, false);
    if (isRelevancySearchEnabled) {
      return <Navigate to={DEFAULT_ROUTE} replace />;
    }
    return <SignalSettings internationalDataAccess={user.company.hasInternationalDataAccess} />;
  },
});

// /account-management/account — the user's own email, password and sign-in methods. Gated on a
// Better Auth session, which every section's endpoints require.
export const accountAccountRoute = createRoute({
  getParentRoute: () => accountLayoutRoute,
  path: "/account",
  staticData: { pageName: "Account Management - Account" },
  component: function AccountAccountRouteComp() {
    const { data: session, isPending } = useSession();
    if (isPending) return <CentredSpinner />;
    if (!session?.user) {
      // Need to login again to get a better-auth session
      return <Navigate to="/auth/login" />;
    }
    return <AccountSettingsPage />;
  },
});

// /account-management/change-email is now a section of the Account page. Kept as a redirect so
// existing links and bookmarks still land somewhere.
export const accountChangeEmailRoute = createRoute({
  getParentRoute: () => accountLayoutRoute,
  path: "/change-email",
  staticData: { pageName: "" },
  component: function AccountChangeEmailRedirect() {
    return <Navigate to={settingsRoutes.account} replace />;
  },
});

// /account-management/security — self-serve 2FA. Gated on the presence of a Better Auth session
// (the page only makes sense with one); sends the user to log in otherwise. Mandatory enrolment
// happens on /auth/2fa-setup instead (see rootRoute.ensure2FASetup).
export const accountSecurityRoute = createRoute({
  getParentRoute: () => accountLayoutRoute,
  path: "/security",
  staticData: { pageName: "Account Management - Security" },
  component: function AccountSecurityRouteComp() {
    const { data: session, isPending } = useSession();
    if (isPending) return <CentredSpinner />;
    if (!session?.user) {
      // Need to login again to get a better-auth session
      return <Navigate to="/auth/login" />;
    }
    return <TwoFactorSettings />;
  },
});

const SsoWrapper = styled.div({
  padding: 24,
});

// /account-management/sso — self-serve SSO configuration. Enterprise SSO is an Expert-plan
// feature licensed to the whole company, so the gate is the company's plan rather than the user's
// own — an IT admin on a freemium seat can still configure it. Users outside an Expert company are
// redirected away (the nav item is hidden for them, but the URL is still typeable). The session
// check below additionally requires a Better Auth session.
export const accountSsoRoute = createRoute({
  getParentRoute: () => accountLayoutRoute,
  path: "/sso",
  staticData: { pageName: "Account Management - SSO" },
  beforeLoad: async ({ context }) => {
    const { currentUser } = await context.queryClient.ensureQueryData(
      currentUserQueryOptions(context.graphQLClient),
    );
    if (!companyHasSubscription(currentUser, SubscriptionEnum.Expert)) {
      throw redirect({ to: DEFAULT_ROUTE });
    }
  },
  component: function AccountSsoLayout() {
    const { data: session, isPending } = useSession();
    if (isPending) return <CentredSpinner />;
    if (!session?.user) {
      // Need to login again to get a better-auth session
      return <Navigate to="/auth/login" />;
    }
    return (
      <SsoWrapper>
        <Outlet />
      </SsoWrapper>
    );
  },
});

export const accountSsoIndexRoute = createRoute({
  getParentRoute: () => accountSsoRoute,
  path: "/",
  staticData: { pageName: "Account Management - SSO" },
  component: SsoSettingsPage,
});

// /account-management/sso/add — layout for the add flow
export const accountSsoAddLayoutRoute = createRoute({
  getParentRoute: () => accountSsoRoute,
  path: "/add",
  staticData: { pageName: "" },
  component: Outlet,
});

// /account-management/sso/add/ — provider selection grid
export const accountSsoAddIndexRoute = createRoute({
  getParentRoute: () => accountSsoAddLayoutRoute,
  path: "/",
  staticData: { pageName: "Account Management - SSO - Add" },
  component: ProviderSelector,
});

// /account-management/sso/add/$presetKey — register a new SSO provider
export const accountSsoAddPresetRoute = createRoute({
  getParentRoute: () => accountSsoAddLayoutRoute,
  path: "/$presetKey",
  staticData: { pageName: "Account Management - SSO - Register" },
  // Once per navigation, not per render: a provider-list refresh while leaving the route must not
  // flip the submitted form into this redirect. Deep-link only — configured presets are disabled.
  beforeLoad: async ({ context, params }) => {
    const preset = getPresetByKey(params.presetKey);
    if (!preset) return;

    let existingProviderId: string;
    try {
      const [{ currentUser }, providers] = await Promise.all([
        context.queryClient.ensureQueryData(currentUserQueryOptions(context.graphQLClient)),
        context.queryClient.ensureQueryData(ssoProvidersQueryOptions()),
      ]);
      if (!currentUser) return;
      existingProviderId = generateProviderId(
        currentUser.company.guid,
        preset.protocol,
        preset.providerName,
      );
      if (!providers.some((p) => p.providerId === existingProviderId)) return;
    } catch {
      // Fail open — registration still rejects a duplicate providerId server-side.
      return;
    }

    throw redirect({
      to: "/account-management/sso/$providerId",
      params: { providerId: existingProviderId },
    });
  },
  component: function SsoRegisterRouteComp() {
    const { presetKey } = accountSsoAddPresetRoute.useParams();
    const preset = getPresetByKey(presetKey);

    if (!preset) {
      return (
        <EmptyState
          type="noResults"
          size="large"
          width="100%"
          heading="Provider not found"
          description="The selected provider preset could not be found."
        />
      );
    }

    switch (preset.protocol) {
      case "saml":
        return <SamlRegistrationForm preset={preset} />;
      case "oidc":
        return <OidcRegistrationForm preset={preset} />;
      default:
        return (
          <EmptyState
            type="loadError"
            size="large"
            width="100%"
            heading="Provider preset could not be loaded"
            description="The selected provider preset could not be loaded. Please try again later."
          />
        );
    }
  },
});

// /account-management/sso/$providerId — edit an existing SSO provider
export const accountSsoEditRoute = createRoute({
  getParentRoute: () => accountSsoRoute,
  path: "/$providerId",
  staticData: { pageName: "Account Management - SSO - Edit" },
  component: function SsoEditRouteComp() {
    const { providerId } = accountSsoEditRoute.useParams();
    const { data: providers, isLoading } = useSsoProviders();

    if (isLoading) return <CentredSpinner />;

    const provider = providers?.find((p) => p.providerId === providerId);
    if (!provider) {
      return (
        <EmptyState
          type="noResults"
          size="large"
          width="100%"
          heading="Provider not found"
          description="The SSO provider could not be found or you do not have permission to view/edit it. You must be a company admin."
        />
      );
    }

    const sharedEditProvider = {
      providerId: provider.providerId,
      issuer: provider.issuer,
      domain: provider.domain,
    };

    if (provider.samlConfig) {
      return (
        <SamlRegistrationForm
          editProvider={{
            ...sharedEditProvider,
            samlConfig: provider.samlConfig,
          }}
        />
      );
    } else if (provider.oidcConfig) {
      return (
        <OidcRegistrationForm
          editProvider={{
            ...sharedEditProvider,
            oidcConfig: provider.oidcConfig,
          }}
        />
      );
    }

    return (
      <EmptyState
        type="loadError"
        size="large"
        width="100%"
        heading="Provider is not a valid SSO provider"
        description="The SSO provider could not be loaded. Please try again later."
      />
    );
  },
});

// /account-management/my-team
export const accountMyTeamRoute = createRoute({
  getParentRoute: () => accountLayoutRoute,
  path: "/my-team",
  staticData: { pageName: "Account Management - My team" },
  component: UserManagement,
});

// /account-management/usage — gated on the team-usage-stats flag, which also hides the nav item.
// The flag reads false until DevCycle initialises, so a direct load waits rather than redirecting.
export const accountUsageRoute = createRoute({
  getParentRoute: () => accountLayoutRoute,
  path: "/usage",
  staticData: { pageName: "Account Management - Usage" },
  component: function AccountUsageRouteComp() {
    const isDevCycleInitialized = useIsDevCycleInitialized();
    const isTeamUsageStatsEnabled = useVariableValue(TEAM_USAGE_STATS, false);
    if (!isDevCycleInitialized) return <CentredSpinner label="Loading usage" />;
    if (!isTeamUsageStatsEnabled) return <Navigate to={DEFAULT_ROUTE} replace />;
    return <UsagePage />;
  },
});

// /account-management/bid-self-serve
export const accountBidSelfServeRoute = createRoute({
  getParentRoute: () => accountLayoutRoute,
  path: "/bid-self-serve",
  staticData: { pageName: "Account Management - Bid self serve" },
  component: BidSelfServe,
});

// /account-management/qualification-report
export const accountQualificationReportRoute = createRoute({
  getParentRoute: () => accountLayoutRoute,
  path: "/qualification-report",
  staticData: { pageName: "Account Management - Edit qualification report questions" },
  component: EditQualificationReportQuestions,
});

// /account-management/quick-prompts
export const accountQuickPromptsRoute = createRoute({
  getParentRoute: () => accountLayoutRoute,
  path: "/quick-prompts",
  staticData: { pageName: "Account Management - Quick prompts" },
  component: QuickPromptsSettings,
});

// /account-management/lead-stages
export const accountLeadStagesRoute = createRoute({
  getParentRoute: () => accountLayoutRoute,
  path: "/lead-stages",
  staticData: { pageName: "Account Management - Lead stages" },
  component: LeadWorkflow,
});

// /account-management/bid-stages
export const accountBidStagesRoute = createRoute({
  getParentRoute: () => accountLayoutRoute,
  path: "/bid-stages",
  staticData: { pageName: "Account Management - Bid stages" },
  component: BidWorkflow,
});

// /account-management/tone-of-voice
export const accountToneOfVoiceRoute = createRoute({
  getParentRoute: () => accountLayoutRoute,
  path: "/tone-of-voice",
  staticData: { pageName: "Account Management - Tone of voice" },
  component: ToneOfVoicePage,
});

// /account-management/cognism-token
export const accountCognismTokenRoute = createRoute({
  getParentRoute: () => accountLayoutRoute,
  path: "/cognism-token",
  staticData: { pageName: "Account Management - Cognism token" },
  component: CognismTokenPage,
});

// /account-management/contacts
export const accountContactsRoute = createRoute({
  getParentRoute: () => accountLayoutRoute,
  path: "/contacts",
  staticData: { pageName: "Account Management - Contacts" },
  component: ContactsSettingsPage,
});

// /account-management/integrations
// Reachable regardless of the CRM integration flag: the page also hosts the MCP and cloud storage
// connections, which the flag has no bearing on. The flag hides the CRM section from within the
// page instead — see Integrations.tsx.
export const accountIntegrationsRoute = createRoute({
  getParentRoute: () => accountLayoutRoute,
  path: "/integrations",
  staticData: { pageName: "Account Management - Integrations" },
  component: () => <Outlet />,
});

// /account-management/integrations/
export const accountIntegrationsIndexRoute = createRoute({
  getParentRoute: () => accountIntegrationsRoute,
  path: "/",
  staticData: { pageName: "Account Management - Integrations" },
  component: Integrations,
});

// /account-management/integrations/setup
// Configures a connected CRM and nothing else, so it stays gated on the CRM integration flag.
export const accountIntegrationsSetupRoute = createRoute({
  getParentRoute: () => accountIntegrationsRoute,
  path: "/setup",
  staticData: { pageName: "Account Management - Integrations" },
  beforeLoad: async ({ context }) => {
    const { currentUser } = await context.queryClient.ensureQueryData(
      currentUserQueryOptions(context.graphQLClient),
    );
    if (!currentUser?.company.allowCrmIntegration) {
      throw redirect({ to: DEFAULT_ROUTE });
    }
  },
  component: IntegrationSetup,
});

// /account-management/integrations/field-mapping
// Configures a CRM connected through Paragon, so it is gated the same way the setup page is.
export const accountIntegrationsFieldMappingRoute = createRoute({
  getParentRoute: () => accountIntegrationsRoute,
  path: "/field-mapping",
  staticData: { pageName: "Account Management - Integrations" },
  beforeLoad: async ({ context }) => {
    const { currentUser } = await context.queryClient.ensureQueryData(
      currentUserQueryOptions(context.graphQLClient),
    );
    if (!currentUser?.company.allowCrmIntegration) {
      throw redirect({ to: DEFAULT_ROUTE });
    }
  },
  component: FieldMappingPage,
});

// /account-management/integrations/mcp — self-serve MCP credentials.
//
// Deliberately not gated on `allowCrmIntegration`: that setting is about CRM sync, and its parent
// route is reachable without it. Not gated on company-admin: any user may manage the company's
// credentials, unless the company keeps that to admins, and the page then shows a member the list
// without its actions.
//
// `tool` says which integration tile the user arrived from, so the generate flow knows what it is
// registering. An unrecognised value falls back to the generic tile rather than erroring.
export const accountIntegrationsMcpRoute = createRoute({
  getParentRoute: () => accountIntegrationsRoute,
  path: "/mcp",
  staticData: { pageName: "Account Management - MCP credentials" },
  validateSearch: (search: Record<string, unknown>): { tool?: McpProviderId } => ({
    tool: isMcpProviderId(search.tool) ? search.tool : undefined,
  }),
  component: function AccountIntegrationsMcp() {
    const { tool } = accountIntegrationsMcpRoute.useSearch();
    const { data: session, isPending } = useSession();
    if (isPending) return <CentredSpinner />;
    if (!session?.user) {
      // These endpoints authenticate with a Better Auth session, which a Rails-only login lacks.
      return <Navigate to="/auth/login" />;
    }
    return <McpCredentialsPage toolId={tool} />;
  },
});

// ---------------------------------------------------------------------------
// /account-management/notifications — NotificationsPage renders the tab nav + Outlet.
// Each tab has its own sub-tree; Slack/Teams additionally have create/edit child routes.
// ---------------------------------------------------------------------------

export const accountNotificationsLayoutRoute = createRoute({
  getParentRoute: () => accountLayoutRoute,
  path: "/notifications",
  staticData: { pageName: "" },
  component: NotificationsPage,
});

export const accountNotificationsEmailRoute = createRoute({
  getParentRoute: () => accountNotificationsLayoutRoute,
  path: "/",
  staticData: { pageName: "Account Management - Notifications" },
  component: EmailNotifications,
});

// Slack sub-tree.
export const accountNotificationsSlackLayoutRoute = createRoute({
  getParentRoute: () => accountNotificationsLayoutRoute,
  path: "/slack",
  staticData: { pageName: "" },
  component: Outlet,
});

export const accountNotificationsSlackIndexRoute = createRoute({
  getParentRoute: () => accountNotificationsSlackLayoutRoute,
  path: "/",
  staticData: { pageName: "Account Management - Notifications - Slack" },
  component: function SlackNotificationsIndex() {
    return <IntegrationIndex type="SLACK" />;
  },
});

export const accountNotificationsSlackCreateRoute = createRoute({
  getParentRoute: () => accountNotificationsSlackLayoutRoute,
  path: "/create",
  staticData: { pageName: "Account Management - Notifications - Slack" },
  component: function SlackNotificationsCreate() {
    return <IntegrationsNotificationSettings type="SLACK" />;
  },
});

export const accountNotificationsSlackEditRoute = createRoute({
  getParentRoute: () => accountNotificationsSlackLayoutRoute,
  path: "/edit/$id",
  staticData: { pageName: "Account Management - Notifications - Slack" },
  component: function SlackNotificationsEdit() {
    const { id } = accountNotificationsSlackEditRoute.useParams();
    return <IntegrationsNotificationSettings type="SLACK" subscriptionId={id} />;
  },
});

// Teams sub-tree — same structure as Slack.
export const accountNotificationsTeamsLayoutRoute = createRoute({
  getParentRoute: () => accountNotificationsLayoutRoute,
  path: "/teams",
  staticData: { pageName: "" },
  component: Outlet,
});

export const accountNotificationsTeamsIndexRoute = createRoute({
  getParentRoute: () => accountNotificationsTeamsLayoutRoute,
  path: "/",
  staticData: { pageName: "Account Management - Notifications - Teams" },
  component: function TeamsNotificationsIndex() {
    return <IntegrationIndex type="TEAMS" />;
  },
});

export const accountNotificationsTeamsCreateRoute = createRoute({
  getParentRoute: () => accountNotificationsTeamsLayoutRoute,
  path: "/create",
  staticData: { pageName: "Account Management - Notifications - Teams" },
  component: function TeamsNotificationsCreate() {
    return <IntegrationsNotificationSettings type="TEAMS" />;
  },
});

export const accountNotificationsTeamsEditRoute = createRoute({
  getParentRoute: () => accountNotificationsTeamsLayoutRoute,
  path: "/edit/$id",
  staticData: { pageName: "Account Management - Notifications - Teams" },
  component: function TeamsNotificationsEdit() {
    const { id } = accountNotificationsTeamsEditRoute.useParams();
    return <IntegrationsNotificationSettings type="TEAMS" subscriptionId={id} />;
  },
});

// ---------------------------------------------------------------------------
// Route tree export
// ---------------------------------------------------------------------------

export const accountRouteTree = accountLayoutRoute.addChildren([
  accountIndexRoute,
  accountSubscriptionRoute,
  accountFeedSettingsRoute,
  accountAccountRoute,
  accountChangeEmailRoute,
  accountSecurityRoute,
  accountSsoRoute.addChildren([
    accountSsoIndexRoute,
    accountSsoAddLayoutRoute.addChildren([accountSsoAddIndexRoute, accountSsoAddPresetRoute]),
    accountSsoEditRoute,
  ]),
  accountMyTeamRoute,
  accountUsageRoute,
  accountNotificationsLayoutRoute.addChildren([
    accountNotificationsEmailRoute,
    accountNotificationsSlackLayoutRoute.addChildren([
      accountNotificationsSlackIndexRoute,
      accountNotificationsSlackCreateRoute,
      accountNotificationsSlackEditRoute,
    ]),
    accountNotificationsTeamsLayoutRoute.addChildren([
      accountNotificationsTeamsIndexRoute,
      accountNotificationsTeamsCreateRoute,
      accountNotificationsTeamsEditRoute,
    ]),
  ]),
  accountBidSelfServeRoute,
  accountQualificationReportRoute,
  accountQuickPromptsRoute,
  accountLeadStagesRoute,
  accountBidStagesRoute,
  accountToneOfVoiceRoute,
  accountCognismTokenRoute,
  accountContactsRoute,
  accountIntegrationsRoute.addChildren([
    accountIntegrationsIndexRoute,
    accountIntegrationsSetupRoute,
    accountIntegrationsFieldMappingRoute,
    accountIntegrationsMcpRoute,
  ]),
]);
